import { Box } from "@mui/material";
import styles from './PreorderSearch.module.scss';
import InputTextField from "../../../components/common/formControls/textField/InputTextField";
import { ReactNode, useState } from "react";
import Combobox from "../../../components/common/formControls/combobox/Combobox";
import NormalButton from "../../../components/common/formControls/buttons/NormalButton";
import SuccessButton from "../../../components/common/formControls/buttons/SuccessButton";
import {useTranslation} from "react-i18next";
import { DateFilter, FilterOption } from "../../../common/Interfaces";
import InputDateTextField from "../../../components/common/formControls/textField/InputDateTextField";
import { getDateInYYYYMMDDFormat, getDateInYYYYMMDDWithoutTimeFormat } from "../../../common/Utils";

interface Props {
    setFiltering?: any;
}

export default function PreorderSearch ({setFiltering}: Props){
    const [preOrderCodeFilter, setPreOrderCodeFilter] = useState<string>("");
    const [orderCodeFilter, setOrderCodeFilter] = useState<string>("");
    const [dateRangeFilter, setDateRangeFilter] = useState<DateFilter>({
                                                    StartDate: getDateInYYYYMMDDFormat(new Date()), 
                                                    EndDate: getDateInYYYYMMDDFormat(new Date())});
    const [preOrderFilterError, setPreOrderFilterError] = useState<string|undefined>(undefined);
    const [orderFilterError, setOrderFilterError] = useState<string|undefined>(undefined);

    const {t} = useTranslation();

    const filterOptions: FilterOption[] = [
        {
            Id: "NoFilter",
            Name: "",
        },
        {
            Id: 'PreOrderCode',
            Name: t("PREORDERCODE")
        },
        {
            Id: 'Code',
            Name: t("ORDERCODE")
        },
        {
            Id: 'DateRange',
            Name: `${t("DATERANGE")} (${t("CREATIONDATE")})`
        },
    ] ;

    const [preOrderFilter, setPreOrderFilter] = useState<FilterOption>(filterOptions[0]);

    const handlePreOrderFilterChange = (_e: any, value: any) => {
        setPreOrderFilterError(undefined);
        setOrderFilterError(undefined);
        setPreOrderFilter(value);
    }

    const  handlePreOrderCodeFilterChange = (e: any) => {
        setPreOrderCodeFilter(e.target.value);
    }

    const handleOrderCodeFilterChange = (e: any) => {
        setOrderCodeFilter(e.target.value);
    }

    const handleStartDateChange = (value: Date) => {
        setDateRangeFilter({
            ...dateRangeFilter,
            StartDate: getDateInYYYYMMDDFormat(value),
        });
    }

    const handleEndDateChange = (value: Date) => {
        setDateRangeFilter({
            ...dateRangeFilter,
            EndDate: getDateInYYYYMMDDFormat(value),
        });
    }

    const showControl = (): ReactNode => {
        switch(preOrderFilter?.Id){
            case "PreOrderCode":
                return <InputTextField id={"PreOrderCodeField"} 
                                       label={""} 
                                       value={preOrderCodeFilter} 
                                       width="225px"
                                       errorMessage={preOrderFilterError}
                                       handleValueChange={handlePreOrderCodeFilterChange} />

            case "Code":
                return <InputTextField id={""} 
                                       label={""} 
                                       value={orderCodeFilter}
                                       width="225px"
                                       errorMessage={orderFilterError}
                                       handleValueChange={handleOrderCodeFilterChange} />

            case "DateRange":
                return <Box className={styles.container}>
                    <InputDateTextField id={"StartDateField"} 
                                        label={t("STARTDATE")}
                                        value={dateRangeFilter?.StartDate}
                                        width="145px"
                                        handleValueChange={handleStartDateChange}/>

                    <InputDateTextField id={"EndDateField"} 
                                        label={t("ENDDATE")}
                                        value={dateRangeFilter?.EndDate}
                                        width="145px"
                                        handleValueChange={handleEndDateChange}/>
                </Box>

            default:
                return <></>
        }
    }

    const handleAddClick = () => {
        window.open(`${window.location.href}preorder`, '_self');
    }

    const handleUpdateClick = () => {
        window.location.reload();
    }

    const handleSearchClick = () => {
        setPreOrderFilterError(undefined);
        setOrderFilterError(undefined);

        switch(preOrderFilter?.Id){
            case "PreOrderCode":
                if (!preOrderCodeFilter || preOrderCodeFilter.length === 0){
                    setPreOrderFilterError(t("EMPTYFIELDERROR"));
                    return;
                }

                setFiltering({
                    Name: preOrderFilter?.Id,
                    FriendlyName: preOrderFilter?.Name,
                    FilterValue: {
                        Value: preOrderCodeFilter,
                    },
                    Value: preOrderCodeFilter,
                });
                break;

            case "Code":
                if (!orderCodeFilter || orderCodeFilter.length === 0){
                    setOrderFilterError(t("EMPTYFIELDERROR"));
                    return;
                }

                setFiltering({
                    Name: preOrderFilter?.Id,
                    FriendlyName: preOrderFilter?.Name,
                    FilterValue: {
                        Value: orderCodeFilter,
                    },
                    Value: orderCodeFilter,
                });
                break;

            case "DateRange":
                setFiltering({
                    Name: preOrderFilter?.Id,
                    FriendlyName: preOrderFilter?.Name,
                    FilterValue: {
                        Value: getDateInYYYYMMDDWithoutTimeFormat(new Date(dateRangeFilter.StartDate), true, false),
                        AlternateValue: getDateInYYYYMMDDWithoutTimeFormat(new Date(dateRangeFilter.EndDate), true, true),
                    },
                    Value: `${dateRangeFilter.StartDate} - ${dateRangeFilter.EndDate}`
                });
                break;

            default:
                setFiltering(undefined);
                break;
        }
        
    }

    return (<Box className={[styles.container, styles.wrap].join(" ")}>
        <Combobox
                fieldId="preOrderFilterSelector"
                fieldLabel={t("SEARCHBY")}
                fieldOptions={filterOptions}
                handleValueChange={handlePreOrderFilterChange}
                fieldValue={preOrderFilter}
                width="300px"
            />
            <Box className={styles.container}>
                { showControl()}
                { preOrderFilter && (<NormalButton disabled={!preOrderFilter || preOrderFilter.Id === "NoFilter"} id="SearchButton" label={t("SEARCH")} onclickHandler={handleSearchClick} />)}
            </Box>
            
            <Box className={[styles.container].join(" ")}>
                <SuccessButton id="AddButton" label={`${t("ADD")} ${t("PREORDER")}`} onclickHandler={handleAddClick} />
                <NormalButton id="UpdateButton" label={t("UPDATELIST")} onclickHandler={handleUpdateClick}/>
            </Box>
    </Box>)
}